<template>
  <component
    :is="tag"
    :to="isRouterLink ? to : undefined"
    :href="isAnchor ? href : undefined"
    :type="isButton ? type : undefined"
    :tabindex="isDisabled ? -1 : tabIndex"
    :class="buttonClasses"
    :aria-disabled="isDisabled ? 'true' : undefined"
    @click="handleClick"
  >
    <template v-if="loading">טוען...</template>
    <slot v-else />
  </component>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { RouterLink } from 'vue-router';

export type LuxuryButtonVariant =
  | 'primary'
  | 'compact'
  | 'outline'
  | 'ghost'
  | 'danger';

export type LuxuryButtonSize = 'sm' | 'md' | 'lg';

export default defineComponent({
  name: 'LuxuryButton',

  props: {
    variant: {
      type: String as PropType<LuxuryButtonVariant>,
      default: 'primary',
    },

    size: {
      type: String as PropType<LuxuryButtonSize>,
      default: 'md',
    },

    to: {
      type: [String, Object],
      default: undefined,
    },

    href: {
      type: String,
      default: undefined,
    },

    type: {
      type: String as PropType<'button' | 'submit' | 'reset'>,
      default: 'button',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    block: {
      type: Boolean,
      default: false,
    },

    tabIndex: {
      type: Number,
      default: undefined,
    },
  },

  emits: ['click', 'onClick'],

  computed: {
    isDisabled(): boolean {
      return this.disabled || this.loading;
    },

    isRouterLink(): boolean {
      return Boolean(this.to) && !this.isDisabled;
    },

    isAnchor(): boolean {
      return Boolean(this.href) && !this.isDisabled && !this.to;
    },

    isButton(): boolean {
      return !this.isRouterLink && !this.isAnchor;
    },

    tag(): string | typeof RouterLink {
      if (this.isRouterLink) {
        return RouterLink;
      }

      if (this.isAnchor) {
        return 'a';
      }

      return 'button';
    },

    buttonClasses(): string[] {
      const classes = [
        'luxury-btn',
        `luxury-btn--${this.variant}`,
        `luxury-btn--${this.size}`,
      ];

      if (this.block) {
        classes.push('luxury-btn--block');
      }

      if (this.isDisabled) {
        classes.push('luxury-btn--disabled');
      }

      if (this.loading) {
        classes.push('luxury-btn--loading');
      }

      return classes;
    },
  },

  methods: {
    handleClick(event: MouseEvent) {
      if (this.isDisabled) {
        event.preventDefault();
        return;
      }

      this.$emit('click', event);
      this.$emit('onClick', event);
    },
  },
});
</script>
